import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import { formatTimestampMonthDayYear } from 'src/utils'
import { getPostTopic } from 'src/utils/hygraph'
import { IPost } from '../../types/hygraph'

type IProps = {
  firstPost: IPost | null
}

function BlogHero({ firstPost }: IProps) {
  const { t } = useTranslation('blog')

  if (!firstPost) return null
  const { basePath, topic } = getPostTopic(firstPost)
  if (!basePath) return null
  const categoryLabel = topic?.label

  const description =
    firstPost?.description && firstPost?.description.length > 220
      ? `${firstPost.description.slice(0, 220).trim()}…`
      : firstPost?.description

  return (
    <section className="border-t border-ink-100/15 pt-10 md:pt-14">
      {/* Eyebrow rail */}
      <div className="md:grid md:grid-cols-12 md:gap-12">
        <div className="md:col-span-4">
          <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
            {t('FEATURED.EYEBROW')}
          </p>
          {categoryLabel && (
            <p className="mt-3 text-[13px] tracking-[0.06em] text-ink-200 uppercase">
              {categoryLabel}
            </p>
          )}
        </div>
        <div className="md:col-span-8" />
      </div>

      {/* Asymmetric editorial layout: image left (7), content right (5) */}
      <Link
        href={`${basePath}/${firstPost?.slug}`}
        className="group mt-6 block md:mt-10 md:grid md:grid-cols-12 md:gap-12"
      >
        <div className="relative aspect-[4/3] w-full overflow-hidden md:col-span-7 md:aspect-[5/4]">
          <Image
            src={firstPost.coverImage?.url ?? ''}
            alt={firstPost.coverImageAlt ?? ''}
            fill
            className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]"
            sizes="(max-width: 900px) 100vw, 58vw"
            quality={75}
            priority
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink-800/30 via-transparent to-transparent" />
          <span className="absolute top-5 left-5 font-display text-[11px] tracking-[0.22em] text-white-500/85 uppercase md:text-xs">
            {t('FEATURED.EYEBROW')}
          </span>
        </div>

        <div className="mt-6 flex flex-col justify-end md:col-span-5 md:mt-0">
          <h2 className="font-display text-[26px] leading-[1.12] font-medium tracking-[-0.01em] text-ink-700 md:text-[40px]">
            {firstPost?.title}
          </h2>

          {description && (
            <p className="mt-5 max-w-md text-[14px] leading-relaxed text-ink-200 md:mt-6 md:text-[15px]">
              {description}
            </p>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-ink-200 md:mt-8 md:text-[13px]">
            {firstPost?.author?.name && (
              <span className="text-ink-700">{firstPost.author.name}</span>
            )}
            {firstPost?.author?.name &&
              (firstPost?.date ?? firstPost?.createdAt) && (
                <span aria-hidden="true" className="text-ink-100/40">
                  ·
                </span>
              )}
            {(firstPost?.date ?? firstPost?.createdAt) && (
              <time dateTime={firstPost.date ?? firstPost.createdAt}>
                {formatTimestampMonthDayYear(
                  firstPost.date ?? firstPost.createdAt
                )}
              </time>
            )}
          </div>

          <span className="mt-6 inline-flex items-center gap-2 self-start text-[13px] font-semibold text-blue-500 underline decoration-blue-500/40 decoration-1 underline-offset-[6px] transition-colors group-hover:decoration-blue-500 md:mt-8 md:text-sm">
            {t('FEATURED.READ_ARTICLE')}
            <span
              aria-hidden="true"
              className="transition-transform duration-300 group-hover:translate-x-1"
            >
              →
            </span>
          </span>
        </div>
      </Link>
    </section>
  )
}

export default BlogHero
